import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

export type FontSize = 'small' | 'medium' | 'large'

const STORAGE_KEY = 'lesson-font-size'
const DEFAULT_SIZE: FontSize = 'medium'

interface FontSizeContextValue {
  size: FontSize
  setSize: (next: FontSize) => void
}

const FontSizeContext = createContext<FontSizeContextValue | null>(null)

function readStored(): FontSize {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'small' || raw === 'medium' || raw === 'large') return raw
  } catch {
    // localStorage may throw in private mode / SSR — fall through to default.
  }
  return DEFAULT_SIZE
}

export function FontSizeProvider({ children }: { children: ReactNode }) {
  const [size, setSizeState] = useState<FontSize>(readStored)

  // Sync to localStorage on every change. Reads happen on mount only.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, size)
    } catch {
      // best-effort: a failed write doesn't roll back the in-memory state.
    }
  }, [size])

  const setSize = useCallback((next: FontSize) => setSizeState(next), [])

  return (
    <FontSizeContext.Provider value={{ size, setSize }}>{children}</FontSizeContext.Provider>
  )
}

export function useFontSize() {
  const ctx = useContext(FontSizeContext)
  if (!ctx) throw new Error('useFontSize must be used within FontSizeProvider')
  return ctx
}

// Maps the abstract size to the Tailwind Typography modifier class. Used by
// LessonMarkdown to scale headings, paragraphs, lists etc. coherently — prose
// scales them as a set rather than us picking individual sizes.
export function fontSizeProseClass(size: FontSize): string {
  switch (size) {
    case 'small':
      return 'prose-sm'
    case 'large':
      return 'prose-lg'
    case 'medium':
    default:
      return 'prose-base'
  }
}
